/**
 * Places and URLs for the construction company directory.
 *
 * Every directory link is built here (regionHref / townHref / companyHref), so
 * no URL is ever hand-typed in a page or component. Safe to import anywhere.
 */

export const DIRECTORY_PATH = "/construction-companies";

export type RegionSlug =
  | "north-east"
  | "north-west"
  | "yorkshire-and-the-humber"
  | "east-midlands"
  | "west-midlands"
  | "east-of-england"
  | "london"
  | "south-east"
  | "south-west"
  | "scotland"
  | "wales"
  | "northern-ireland";

export type Region = {
  slug: RegionSlug;
  /** Display name, e.g. "North West". */
  name: string;
  /** Name used after "in", e.g. "the North West", "Scotland". */
  inName: string;
  /** Longer name for headings and search, e.g. "North West England". */
  longName: string;
  /** ONS area code the boundary came from. */
  code: string;
  nation: "England" | "Scotland" | "Wales" | "Northern Ireland";
};

export const REGIONS: Region[] = [
  { slug: "north-east", name: "North East", inName: "the North East", longName: "North East England", code: "E12000001", nation: "England" },
  { slug: "north-west", name: "North West", inName: "the North West", longName: "North West England", code: "E12000002", nation: "England" },
  { slug: "yorkshire-and-the-humber", name: "Yorkshire and the Humber", inName: "Yorkshire and the Humber", longName: "Yorkshire and the Humber", code: "E12000003", nation: "England" },
  { slug: "east-midlands", name: "East Midlands", inName: "the East Midlands", longName: "East Midlands", code: "E12000004", nation: "England" },
  { slug: "west-midlands", name: "West Midlands", inName: "the West Midlands", longName: "West Midlands", code: "E12000005", nation: "England" },
  { slug: "east-of-england", name: "East of England", inName: "the East of England", longName: "East of England", code: "E12000006", nation: "England" },
  { slug: "london", name: "London", inName: "London", longName: "Greater London", code: "E12000007", nation: "England" },
  { slug: "south-east", name: "South East", inName: "the South East", longName: "South East England", code: "E12000008", nation: "England" },
  { slug: "south-west", name: "South West", inName: "the South West", longName: "South West England", code: "E12000009", nation: "England" },
  { slug: "scotland", name: "Scotland", inName: "Scotland", longName: "Scotland", code: "S92000003", nation: "Scotland" },
  { slug: "wales", name: "Wales", inName: "Wales", longName: "Wales", code: "W92000004", nation: "Wales" },
  { slug: "northern-ireland", name: "Northern Ireland", inName: "Northern Ireland", longName: "Northern Ireland", code: "N92000002", nation: "Northern Ireland" },
];

const REGION_BY_SLUG = new Map<string, Region>(REGIONS.map((r) => [r.slug, r]));

export function getRegion(slug: string | null | undefined): Region | null {
  if (!slug) return null;
  return REGION_BY_SLUG.get(slug) ?? null;
}

export function isRegionSlug(value: unknown): value is RegionSlug {
  return typeof value === "string" && REGION_BY_SLUG.has(value);
}

/** URL-safe slug: lower case, ASCII, hyphenated. Shared by the import job and every page. */
export function slugify(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/['’"]+/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80)
    .replace(/-+$/g, "");
}

/** "north-west:manchester". The region is part of the key so towns never collide across regions. */
export function townKey(region: RegionSlug, townSlug: string): string {
  return `${region}:${townSlug}`;
}

export function parseTownKey(key: string): { region: RegionSlug; townSlug: string } | null {
  const [region, townSlug] = key.split(":");
  if (!isRegionSlug(region) || !townSlug) return null;
  return { region, townSlug };
}

export function directoryHref(): string {
  return DIRECTORY_PATH;
}

export function regionHref(region: RegionSlug | string): string {
  return `${DIRECTORY_PATH}/${region}`;
}

export function townHref(region: RegionSlug | string, townSlug: string): string {
  return `${DIRECTORY_PATH}/${region}/${townSlug}`;
}

/** Companies per numbered town page (the search API's maximum). */
export const TOWN_PAGE_SIZE = 60;

/** One numbered page of every company in a town: /construction-companies/<region>/<town>/page/<n>. */
export function townPageHref(region: RegionSlug | string, townSlug: string, page: number): string {
  return `${townHref(region, townSlug)}/page/${page}`;
}

export function townHrefFromKey(key: string): string {
  const parsed = parseTownKey(key);
  return parsed ? townHref(parsed.region, parsed.townSlug) : DIRECTORY_PATH;
}

export function companyHref(region: RegionSlug | string, townSlug: string, companySlug: string): string {
  return `${DIRECTORY_PATH}/${region}/${townSlug}/${companySlug}`;
}
